import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import httpClient from '../services/httpClient';
import { Banner, BannerPlacement, SHOWCASE_FILTERS, showcaseCatalogUrl } from '@/features/banners/types';

const AnnouncementCarousel = ({ placement = 'home' }: { placement?: BannerPlacement }) => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState<Banner[]>([]);

  const autoplay = useRef(Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true }));
  // loop + центрирование: активный слайд по центру, соседние выглядывают по краям.
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'center' }, [autoplay.current]);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    setBanners([]);
    httpClient
      .get<Banner[]>('/api/Banners', { params: { placement } })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setBanners(list.filter(b => b.imageUrl && (placement !== 'playstation' || (b.showcaseFilter && Object.prototype.hasOwnProperty.call(SHOWCASE_FILTERS, b.showcaseFilter)))));
      })
      .catch(() => { /* карусель необязательна — молча скрываем при ошибке */ });
    return () => { cancelled = true; };
  }, [placement]);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    // Слайды приезжают асинхронно — пересобираем Embla под актуальный список.
    emblaApi.reInit();
    setSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect, banners]);

  const handleClick = (banner: Banner) => {
    if (placement === 'playstation' && banner.showcaseFilter) {
      navigate(showcaseCatalogUrl(banner.showcaseFilter));
    } else if (banner.isSharing) {
      navigate('/catalog?sharing=true');
    } else if (banner.categorySlug) {
      navigate(`/category/${banner.categorySlug}`);
    } else if (banner.externalUrl) {
      window.open(banner.externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (banners.length === 0) return null;

  return (
    <section className="mb-10">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {banners.map((item, i) => {
            const clickable = Boolean(item.showcaseFilter || item.isSharing || item.categorySlug || item.externalUrl);
            const isActive = i === selected;
            return (
              <div
                key={item.id}
                className="min-w-0 shrink-0 grow-0 basis-[78%] pl-1 sm:basis-[60%] lg:basis-[42%] xl:basis-[34%]"
              >
                <button
                  type="button"
                  onClick={() => handleClick(item)}
                  disabled={!clickable}
                  aria-label={item.showcaseFilter ? SHOWCASE_FILTERS[item.showcaseFilter] : 'Открыть баннер'}
                  className={`block aspect-square w-full max-w-[476px] overflow-hidden rounded-2xl transition-transform duration-300 will-change-transform ${
                    isActive ? 'scale-100' : 'scale-[0.82]'
                  }`}
                >
                  <img
                    src={item.imageUrl ?? ''}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Полоски-пагинация: активная подсвечена. */}
      {snaps.length > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2 px-8">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Слайд ${i + 1}`}
              onClick={() => emblaApi?.scrollTo(i)}
              className={`h-1 w-8 rounded-md transition-all md:w-16 ${
                i === selected ? 'bg-foreground' : 'bg-foreground/10'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default AnnouncementCarousel;
